import { z } from 'zod';
import { PaymentChannelSchema } from './payment.js';
// ============================================================
// 고객센터 문의 (Contact Inquiry) — 스레드형 (FIKET-338)
//
// 문의 1건(contact_inquiries) 아래 메시지(contact_inquiry_messages)가
// 고객 ↔ 관리자 순으로 쌓인다. 로그인 필수.
// ============================================================
// ── 카테고리 ───────────────────────────────────────────────
export const ContactInquiryCategorySchema = z.enum([
    'BUG',
    'PAYMENT',
    'ACCOUNT',
    'FEATURE',
    'OTHER',
]);
export const CONTACT_INQUIRY_CATEGORY_LABELS = {
    BUG: '버그/오류',
    PAYMENT: '결제',
    ACCOUNT: '계정',
    FEATURE: '기능 제안',
    OTHER: '기타',
};
// ── 상태 ───────────────────────────────────────────────────
// 단일 enum, 라벨은 어드민/고객 관점으로 분리.
export const ContactInquiryStatusSchema = z.enum([
    'OPEN', // 접수, 미답변
    'ANSWERED', // 관리자 답변 있음
    'CUSTOMER_REPLIED', // 답변 후 고객 재질문
    'CLOSED', // 관리자 종결
]);
/** 어드민 화면용 라벨. "완료" 는 CLOSED 에만 — ANSWERED 는 고객 확인을 기다리는 중간 상태다 (FIKET-439). */
export const CONTACT_INQUIRY_STATUS_LABELS = {
    OPEN: '접수',
    ANSWERED: '답변함',
    CUSTOMER_REPLIED: '재문의',
    CLOSED: '종결',
};
/** 고객 화면용 라벨 */
export const CONTACT_INQUIRY_STATUS_CUSTOMER_LABELS = {
    OPEN: '답변 대기',
    ANSWERED: '답변 완료',
    CUSTOMER_REPLIED: '답변 대기',
    CLOSED: '완료',
};
/** "미답변" — 어드민 배지·대시보드 KPI 기준 */
export const CONTACT_INQUIRY_AWAITING_REPLY_STATUSES = [
    'OPEN',
    'CUSTOMER_REPLIED',
];
export function isInquiryAwaitingReply(status) {
    return CONTACT_INQUIRY_AWAITING_REPLY_STATUSES.includes(status);
}
// ── 우선순위 ───────────────────────────────────────────────
export const ContactInquiryPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export const CONTACT_INQUIRY_PRIORITY_LABELS = {
    LOW: '낮음',
    NORMAL: '보통',
    HIGH: '높음',
    URGENT: '긴급',
};
// ── 종료 주체 / 자동 종료 (FIKET-439) ──────────────────────
// 완료(CLOSED) 기준 3가지: 고객이 "문의 종료" · 관리자 종결 · 마지막 답변 후 N일 무반응(cron).
export const ContactInquiryClosedBySchema = z.enum(['CUSTOMER', 'ADMIN', 'SYSTEM']);
export const CONTACT_INQUIRY_CLOSED_BY_LABELS = {
    CUSTOMER: '고객 종료',
    ADMIN: '관리자 종결',
    SYSTEM: '자동 종료',
};
/**
 * 마지막 관리자 답변 후 고객 반응이 없으면 자동 종료되는 일수.
 * 고객 화면 안내 문구와 cron(/api/cron/close-stale-inquiries)이 같은 값을 쓴다. 별표(중요) 건은 제외.
 */
export const CONTACT_INQUIRY_AUTO_CLOSE_AFTER_DAYS = 7;
// ── 메시지 ─────────────────────────────────────────────────
export const ContactMessageAuthorTypeSchema = z.enum(['CUSTOMER', 'ADMIN', 'SYSTEM']);
/** 메시지당 첨부 이미지 최대 장수 */
export const CONTACT_INQUIRY_MAX_ATTACHMENTS = 5;
/**
 * 상담 운영시간 라벨 — 웹 안내 문구와 서버(business-hours.ts) 라벨의 단일 소스.
 * 실제 판정 숫자(BUSINESS_HOUR_START/END)를 바꾸면 여기도 같이 바꾼다(서버 테스트가 대조).
 */
export const CONTACT_BUSINESS_HOURS_LABEL = '평일 10:00 ~ 17:00';
/**
 * 첨부 이미지 1장의 최대 크기(byte) — 문의 전용 한도.
 * 갤러리 업로드(GALLERY_CONFIG.MAX_FILE_SIZE, 5MB)와 별개다. 브라우저가 먼저 압축해 보내므로
 * 실제로 이 한도에 걸리는 건 압축이 실패한 초대형 파일뿐이다.
 */
export const CONTACT_INQUIRY_MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;
/**
 * 첨부 이미지 1장. url 은 `/api/upload/inquiry` 가 돌려준 공개 프록시 경로(`/api/uploads/<userId>/inquiry/...`).
 * 서버는 생성/재질문 시 url 이 요청자 본인 prefix 인지 검증한다.
 */
export const ContactInquiryAttachmentSchema = z.object({
    url: z.string().min(1).max(512),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    /** 저장된 파일 크기(byte) */
    size: z.number().int().nonnegative().optional(),
});
/** 요청 측 첨부 목록 — 생략 가능(첨부 없음). 서버는 `?? []` 로 정규화한다. */
const AttachmentsInputSchema = z
    .array(ContactInquiryAttachmentSchema)
    .max(CONTACT_INQUIRY_MAX_ATTACHMENTS, `사진은 최대 ${CONTACT_INQUIRY_MAX_ATTACHMENTS}장까지 첨부할 수 있습니다`)
    .optional();
export const ContactInquiryMessageSchema = z.object({
    id: z.string(),
    authorType: ContactMessageAuthorTypeSchema,
    body: z.string(),
    attachments: z.array(ContactInquiryAttachmentSchema).default([]),
    createdAt: z.string(),
    /** ADMIN 메시지의 고객 이메일 발송 시각 (어드민 응답에서만 의미 있음) */
    emailSentAt: z.string().nullable().optional(),
});
// ── AI 부가 정보 ───────────────────────────────────────────
export const ContactInquiryAiTagsSchema = z.object({
    category: ContactInquiryCategorySchema,
    priority: ContactInquiryPrioritySchema,
    reason: z.string(),
    model: z.string(),
    taggedAt: z.string(),
});
export const ContactInquiryAiDiagnosisSchema = z.object({
    summary: z.string(),
    facts: z.array(z.object({ label: z.string(), value: z.string() })),
    suspectedCauses: z.array(z.string()),
    suggestedActions: z.array(z.string()),
    model: z.string(),
    generatedAt: z.string(),
});
// ============================================================
// 고객 측 — 내 문의
// ============================================================
const InquiryBodySchema = z
    .string()
    .min(10, '문의 내용을 10자 이상 입력하세요')
    .max(5000, '문의 내용은 5000자를 초과할 수 없습니다');
/** 문의 생성. name/email 생략 시 서버가 프로필 값으로 채운다. */
export const CreateContactInquiryRequestSchema = z.object({
    category: ContactInquiryCategorySchema,
    message: InquiryBodySchema,
    name: z.string().trim().min(1, '이름을 입력하세요').max(50, '이름은 50자를 초과할 수 없습니다').optional(),
    email: z.string().email('올바른 이메일을 입력하세요').max(255).optional(),
    /** 챗봇에서 "문의 남기기"로 진입한 경우 — 대화 transcript 를 첨부한다. */
    chatbotSessionId: z.string().cuid2().optional(),
    /** 첨부 이미지 (선택, 최대 5장). 첫 고객 메시지에 붙는다. */
    attachments: AttachmentsInputSchema,
});
export const CreateContactInquiryResponseSchema = z.object({
    id: z.string().cuid2(),
});
/** 고객 재질문 (스레드에 메시지 추가). CLOSED 문의에는 불가(409). */
export const AddContactInquiryMessageRequestSchema = z
    .object({
    body: z.string().max(5000, '메시지는 5000자를 초과할 수 없습니다').default(''),
    /** 첨부 이미지 (선택, 최대 5장). 사진만 보내는 메시지도 허용 — 본문은 비어도 된다. */
    attachments: AttachmentsInputSchema,
})
    .refine((d) => d.body.trim().length > 0 || (d.attachments?.length ?? 0) > 0, {
    message: '메시지를 입력하거나 사진을 첨부하세요',
    path: ['body'],
});
/** 목록 탭 — active: CLOSED 제외, closed: CLOSED 만 */
export const ContactInquiryListFilterSchema = z.enum(['active', 'closed']);
/** 목록 카드 1건 */
export const ContactInquiryListItemSchema = z.object({
    id: z.string().cuid2(),
    category: ContactInquiryCategorySchema,
    status: ContactInquiryStatusSchema,
    createdAt: z.string(),
    /** 마지막 메시지 시각 (없으면 createdAt) */
    lastMessageAt: z.string(),
    /** 마지막 메시지 앞 80자 */
    lastMessagePreview: z.string(),
    lastMessageAuthorType: ContactMessageAuthorTypeSchema,
    /** 마지막 메시지의 첨부 사진 장수 (본문이 비면 서버가 미리보기를 '사진 N장' 으로 채운다) */
    lastMessageAttachmentCount: z.number().int().optional(),
    /** 고객이 아직 읽지 않은 관리자 답변이 있는지 */
    hasUnreadReply: z.boolean(),
    messageCount: z.number().int(),
});
/** 상세 — 메시지 시간순. 조회 시 서버가 customer_last_read_at 을 갱신한다. */
export const ContactInquiryDetailSchema = z.object({
    id: z.string().cuid2(),
    category: ContactInquiryCategorySchema,
    status: ContactInquiryStatusSchema,
    name: z.string(),
    email: z.string(),
    createdAt: z.string(),
    closedAt: z.string().nullable(),
    /** 누가 완료했는지 — 고객 종료 / 관리자 종결 / 자동 종료. CLOSED 가 아니면 null */
    closedBy: ContactInquiryClosedBySchema.nullable(),
    /** 챗봇에서 진입한 경우의 대화 스냅샷 (없으면 null) */
    chatbotTranscript: z.string().nullable(),
    messages: z.array(ContactInquiryMessageSchema),
});
/** POST /api/contact/inquiries/:id/close 응답 — 고객이 직접 종료 (ANSWERED 에서만 허용) */
export const CustomerCloseContactInquiryResponseSchema = z.object({
    status: z.literal('CLOSED'),
    closedAt: z.string(),
});
export const ContactInquiryUnreadCountSchema = z.object({
    count: z.number().int(),
});
/** 작성 전 FAQ 자동 제안 (Phase 6) */
export const FaqSuggestRequestSchema = z.object({
    text: z.string().trim().min(15).max(2000),
});
export const FaqSuggestResponseSchema = z.object({
    suggestions: z.array(z.object({
        id: z.string(),
        question: z.string(),
        answer: z.string(),
    })),
});
// ============================================================
// 어드민 측
// ============================================================
export const AdminContactInquiryListItemSchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().nullable(),
    userEmail: z.string().nullable(),
    name: z.string(),
    email: z.string(),
    category: ContactInquiryCategorySchema,
    status: ContactInquiryStatusSchema,
    priority: ContactInquiryPrioritySchema,
    /** 첫 고객 메시지 앞부분 */
    message: z.string(),
    /** 마지막 메시지 앞 120자 */
    lastMessagePreview: z.string(),
    lastMessageAuthorType: ContactMessageAuthorTypeSchema,
    lastMessageAt: z.string(),
    /** 어드민이 마지막 고객 메시지 이후 열어보지 않았는지 */
    unreadForAdmin: z.boolean(),
    messageCount: z.number().int(),
    aiTags: ContactInquiryAiTagsSchema.nullable(),
    chatbotSessionId: z.string().nullable(),
    createdAt: z.string(),
    closedAt: z.string().nullable(),
    closedBy: ContactInquiryClosedBySchema.nullable(),
    /** 별표(중요) 켠 시각. null = 꺼짐 */
    starredAt: z.string().nullable(),
    /** 접수 요청 Origin/Referer 의 hostname (w.fiket.ai = 스마트스토어 채널). 0099 이전 행은 null.
     *  optional: 어드민 번들이 API 보다 먼저 배포되는 창에서 구버전 응답을 받아도 화면이 깨지지 않게 */
    sourceHost: z.string().nullable().optional(),
    /** 문의자의 구매 채널(정산 결제 기준, SITE→SMARTSTORE 순). 빈 배열 = 미결제. optional 이유는 sourceHost 와 같음 */
    purchaseChannels: z.array(PaymentChannelSchema).optional(),
});
export const AdminContactInquiryDetailSchema = z.object({
    id: z.string().cuid2(),
    userId: z.string().nullable(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    name: z.string(),
    email: z.string(),
    category: ContactInquiryCategorySchema,
    status: ContactInquiryStatusSchema,
    priority: ContactInquiryPrioritySchema,
    priorityOverriddenAt: z.string().nullable(),
    /** 첫 고객 메시지 본문 (헤더 복제본) */
    message: z.string(),
    userAgent: z.string().nullable(),
    ipHash: z.string().nullable(),
    notificationSent: z.boolean(),
    chatbotSessionId: z.string().nullable(),
    chatbotTranscript: z.string().nullable(),
    aiTags: ContactInquiryAiTagsSchema.nullable(),
    aiDiagnosis: ContactInquiryAiDiagnosisSchema.nullable(),
    customerLastReadAt: z.string().nullable(),
    adminLastReadAt: z.string().nullable(),
    lastMessageAt: z.string().nullable(),
    createdAt: z.string(),
    closedAt: z.string().nullable(),
    closedBy: ContactInquiryClosedBySchema.nullable(),
    starredAt: z.string().nullable(),
    /** 접수 요청 Origin/Referer 의 hostname (w.fiket.ai = 스마트스토어 채널). 0099 이전 행은 null. optional 은 배포 창 호환용 */
    sourceHost: z.string().nullable().optional(),
    /** 문의자의 구매 채널(정산 결제 기준, SITE→SMARTSTORE 순). 빈 배열 = 미결제. optional 은 배포 창 호환용 */
    purchaseChannels: z.array(PaymentChannelSchema).optional(),
    messages: z.array(ContactInquiryMessageSchema.extend({
        authorUserId: z.string().nullable(),
        authorEmail: z.string().nullable(),
        emailError: z.string().nullable(),
        aiDraftUsed: z.boolean(),
    })),
});
/** 어드민 답변 등록 = ADMIN 메시지 추가 + (옵션) 고객 이메일 발송 + 상태 ANSWERED */
export const AdminReplyContactInquiryRequestSchema = z.object({
    body: z.string().min(1, '내용을 입력하세요').max(20000, '내용은 20000자를 초과할 수 없습니다'),
    /** 이메일 제목. 생략 시 서버 기본값. */
    subject: z.string().trim().min(1).max(200).optional(),
    sendEmail: z.boolean().default(true),
    aiDraftUsed: z.boolean().default(false),
});
export const AdminReplyContactInquiryResponseSchema = z.object({
    id: z.string().cuid2(),
    messageId: z.string(),
    status: ContactInquiryStatusSchema,
    emailSent: z.boolean(),
    emailError: z.string().nullable(),
});
// ── 어드민 답변 템플릿 ──────────────────────────────────────
// 운영자가 미리 써둔 답변 문구. category 가 null 이면 모든 카테고리 공용.
// body 의 {{name}} 은 클라이언트가 문의자 이름으로 치환한다.
export const CONTACT_REPLY_TEMPLATE_NAME_VAR = '{{name}}';
export const ContactReplyTemplateSchema = z.object({
    id: z.string(),
    title: z.string(),
    category: ContactInquiryCategorySchema.nullable(),
    /** 함께 채울 이메일 제목. null 이면 제목은 건드리지 않는다. */
    subject: z.string().nullable(),
    body: z.string(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    updatedBy: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateContactReplyTemplateRequestSchema = z.object({
    title: z.string().trim().min(1, '제목을 입력하세요').max(100, '제목은 100자를 초과할 수 없습니다'),
    category: ContactInquiryCategorySchema.nullable().default(null),
    subject: z.string().trim().max(200, '이메일 제목은 200자를 초과할 수 없습니다').nullable().default(null),
    body: z.string().min(1, '내용을 입력하세요').max(20000, '내용은 20000자를 초과할 수 없습니다'),
    sortOrder: z.number().int().default(0),
    isActive: z.boolean().default(true),
});
/**
 * 부분 수정. Create 스키마의 .partial() 을 쓰지 않는다 — zod v4 는 optional 안의 default 를 그대로
 * 적용해서 빈 PATCH 가 기본값(category null, isActive true …)으로 덮어쓰게 된다.
 */
export const UpdateContactReplyTemplateRequestSchema = z.object({
    title: z.string().trim().min(1, '제목을 입력하세요').max(100, '제목은 100자를 초과할 수 없습니다').optional(),
    category: ContactInquiryCategorySchema.nullable().optional(),
    subject: z.string().trim().max(200, '이메일 제목은 200자를 초과할 수 없습니다').nullable().optional(),
    body: z.string().min(1, '내용을 입력하세요').max(20000, '내용은 20000자를 초과할 수 없습니다').optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
});
/** 어드민 AI 답변 초안 요청 (FIKET-343). SSE 스트림 응답. */
export const AdminContactInquiryAiDraftRequestSchema = z.object({
    /** 어조. 기본 formal(정중한 존댓말). friendly 는 조금 더 부드러운 상담 톤. */
    tone: z.enum(['formal', 'friendly']).default('formal'),
    /** 운영자 추가 지시 (예: "환불 불가 사유를 정중히 설명"). */
    instruction: z.string().trim().max(500, '지시는 500자를 초과할 수 없습니다').optional(),
});
/** 어드민 AI 문의자 진단 (FIKET-344 §6-2). `?force=1` 이면 캐시 무시하고 재생성. */
export const AdminContactInquiryAiDiagnoseResponseSchema = z.object({
    diagnosis: ContactInquiryAiDiagnosisSchema,
    /** true 면 저장된 결과를 그대로 반환(모델 호출 없음) */
    cached: z.boolean(),
});
/** 어드민 AI 재태깅 (FIKET-344 §6-4). 수동 우선순위 지정이 있으면 priority 컬럼은 건드리지 않는다. */
export const AdminContactInquiryAiTagResponseSchema = z.object({
    aiTags: ContactInquiryAiTagsSchema,
    /** 태깅 후 실제 priority 컬럼 값 */
    priority: ContactInquiryPrioritySchema,
    /** AI 제안으로 priority 컬럼이 바뀌었는지 */
    priorityUpdated: z.boolean(),
});
/** 어드민 상태 변경 — 종결 또는 재오픈만 허용. ANSWERED/CUSTOMER_REPLIED 는 메시지로만 전이. */
export const UpdateContactInquiryStatusRequestSchema = z.object({
    status: z.enum(['OPEN', 'CLOSED']),
});
export const UpdateContactInquiryTagsRequestSchema = z
    .object({
    priority: ContactInquiryPrioritySchema.optional(),
    category: ContactInquiryCategorySchema.optional(),
    /** 별표(중요) 켬/끔. false 도 유효한 변경이다 */
    starred: z.boolean().optional(),
})
    .refine((v) => v.priority !== undefined || v.category !== undefined || v.starred !== undefined, {
    message: '변경할 항목이 없습니다',
});
/** 슬롯 종류 — users.slots_* 컬럼과 1:1 */
export const AdminGrantSlotTypeSchema = z.enum(['universal', 'invitation', 'thankyou']);
/** 지급(grant) / 회수(revoke) — 미지정은 지급(구버전 클라이언트 호환) */
export const AdminContactInquiryGrantDirectionSchema = z.enum(['grant', 'revoke']);
/**
 * 문의 상세에서 문의자에게 크레딧/슬롯 보상 지급 또는 회수 (FIKET-387 → 회수 확장).
 * memo 는 원장 description 과 감사로그에 남아 CS 추적에 쓰인다. 회수(direction=revoke)는 서버가 memo 를 필수로 검증한다.
 * 회수량이 잔액보다 크면 잔액까지만 회수한다(회원 상세 revoke_credits 와 동일).
 */
export const AdminContactInquiryGrantRequestSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('credits'),
        direction: AdminContactInquiryGrantDirectionSchema.default('grant'),
        amount: z.number().int().min(1, '1 이상 입력하세요').max(100, '1회 최대 100크레딧'),
        memo: z.string().trim().max(200, '메모는 200자를 초과할 수 없습니다').optional(),
    }),
    z.object({
        kind: z.literal('slots'),
        direction: AdminContactInquiryGrantDirectionSchema.default('grant'),
        slotType: AdminGrantSlotTypeSchema,
        amount: z.number().int().min(1, '1 이상 입력하세요').max(10, '1회 최대 10슬롯'),
        memo: z.string().trim().max(200, '메모는 200자를 초과할 수 없습니다').optional(),
    }),
]);
export const AdminContactInquiryGrantResponseSchema = z.object({
    id: z.string(),
    userId: z.string(),
    kind: z.enum(['credits', 'slots']),
    direction: AdminContactInquiryGrantDirectionSchema,
    /** 요청 수량 */
    amount: z.number().int(),
    /** 실제 회수량 (direction=revoke) — 잔액 부족이면 amount 보다 작을 수 있음 */
    revoked: z.number().int().optional(),
    slotType: AdminGrantSlotTypeSchema.optional(),
    /** 처리 후 크레딧 잔액 (kind=credits) */
    aiCredits: z.number().int().optional(),
    /** 처리 후 슬롯 잔량 (kind=slots) */
    slots: z
        .object({ universal: z.number().int(), invitation: z.number().int(), thankyou: z.number().int() })
        .optional(),
});
// ── 문의자 AI 사진 전체 이메일 발송 ──
// 인앱 브라우저(인스타그램·네이버앱·카카오톡)에서 다운로드가 되지 않는 고객에게 운영자가 수동으로
// zip·개별 사진 presigned 링크를 메일로 보낸다. POST /api/admin/contact-inquiries/:id/ai-photos/send-email
export const AdminContactInquiryAiPhotosEmailResponseSchema = z.object({
    id: z.string(),
    userId: z.string(),
    /** 발송한 수신 주소(문의 접수 이메일) */
    sentTo: z.string(),
    photoCount: z.number().int(),
    zipCount: z.number().int(),
    /** 링크 만료 시각 (ISO) */
    expiresAt: z.string(),
    /** 스레드에 남긴 SYSTEM 메시지 id */
    messageId: z.string(),
});
export const ContactInquiryCountsSchema = z.object({
    /** OPEN + CUSTOMER_REPLIED */
    needsReply: z.number().int(),
    open: z.number().int(),
    customerReplied: z.number().int(),
    answered: z.number().int(),
    closed: z.number().int(),
    /** 별표(중요) 켜진 문의 수 — status 무관 */
    starred: z.number().int(),
});
// ── 문의자 AI 사진 열람 (참조사진 + 생성 이력) ──
// GET /api/admin/contact-inquiries/:id/ai-photos — 문의 처리 중 "얼굴이 안 닮았다·사진이 안 나왔다" 판단용.
// 열람 사유 입력은 없고(문의 처리 자체가 사유) 서버가 감사로그에 문의 ID·대상 회원을 기록한다.
/**
 * 참조사진 품질 지표(FIKET-442) — ai_reference_photos.quality_metrics(jsonb)에서 파생한 스칼라만.
 * 생체정보 원본이 아니라 노출 가능. 도입 시점 이전 기록은 해당 키가 null.
 * 어드민 참조사진 품질 페이지(/api/admin/ai-reference-photos)의 metrics 와 같은 모양.
 */
export const AdminReferencePhotoQualityMetricsSchema = z.object({
    confidence: z.number().nullable(),
    yaw: z.number().nullable(),
    pitch: z.number().nullable(),
    roll: z.number().nullable(),
    brightness: z.number().nullable(),
    sharpness: z.number().nullable(),
    faceSize: z.number().nullable(),
    /** FIKET-433 이후 */
    facePx: z.number().nullable(),
    imageWidth: z.number().nullable(),
    imageHeight: z.number().nullable(),
    /** 크롭 감지 도입 이후 */
    faceMargin: z.number().nullable(),
    /** FIKET-443 이후 — 가시성("해당함" 신뢰도)·피사체 */
    eyesClosed: z.number().nullable(),
    faceOccluded: z.number().nullable(),
    sunglasses: z.number().nullable(),
    eyeglasses: z.number().nullable(),
    gaze: z.number().nullable(),
    animal: z.number().nullable(),
    animalLabels: z.array(z.string()),
    personCount: z.number().nullable(),
});
export const AdminReferencePhotoQualityVerdictSchema = z.enum(['ok', 'warn', 'block']);
export const AdminInquiryReferencePhotoSchema = z.object({
    id: z.string(),
    /** GROOM | BRIDE (업로드 라우트 저장값) */
    role: z.string(),
    originalUrl: z.string(),
    faceDetected: z.boolean().nullable(),
    createdAt: z.string(),
    // ── FIKET-442 검수 판정 (구 API 응답에는 없을 수 있어 optional) ──
    /** 업로드 당시 임계값 기준 판정. 검수 도입 전 레거시 행은 null */
    qualityVerdict: AdminReferencePhotoQualityVerdictSchema.nullable().optional(),
    /** 경고 사유 키 (REASON_LABELS 로 라벨링) — 사용자가 안내를 보고도 진행한 항목 */
    qualityWarnings: z.array(z.string()).optional(),
    /** 거부 사유 키 — 활성 참조사진에는 항상 빈 배열 */
    qualityBlocks: z.array(z.string()).optional(),
    metrics: AdminReferencePhotoQualityMetricsSchema.optional(),
});
/** 검수 거부(block)된 업로드 이력 — S3 원본은 즉시 삭제돼 이미지 없음, 사유·지표만 */
export const AdminInquiryRejectedReferenceSchema = z.object({
    id: z.string(),
    role: z.string(),
    createdAt: z.string(),
    qualityWarnings: z.array(z.string()),
    qualityBlocks: z.array(z.string()),
    metrics: AdminReferencePhotoQualityMetricsSchema,
});
export const AdminInquiryGenerationSchema = z.object({
    id: z.string(),
    /** ai_activity_status */
    status: z.string(),
    style: z.string().nullable(),
    creditsUsed: z.number().nullable(),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
    isAdminTest: z.boolean(),
    /** 이 생성에 쓰인 참조사진 (details.originalUrls, 없으면 [originalUrl]) */
    originalUrls: z.array(z.string()),
    generatedUrls: z.array(z.string()),
    /** generatedUrls 와 같은 인덱스의 800px 썸네일. 없으면 generatedUrls 를 그대로 */
    thumbUrls: z.array(z.string()),
    selectedUrl: z.string().nullable(),
    favoritedUrls: z.array(z.string()),
});
export const AdminInquiryAiPhotosSchema = z.object({
    userId: z.string(),
    /** 보관 중(isActive)이고 원본이 있는 참조사진 전체 (최근순) */
    referencePhotos: z.array(AdminInquiryReferencePhotoSchema),
    /** 보관기간 종료·교체로 비활성화된(isActive=false, 거부 제외) 참조사진 수 — 원본 열람 대상이 아니라 개수만 안내 */
    expiredReferenceCount: z.number().int(),
    /** 검수 거부된 업로드 이력(최근순, 최대 20건). 구 API 응답에는 없을 수 있어 optional */
    rejectedReferences: z.array(AdminInquiryRejectedReferenceSchema).optional(),
    /** type=generation 이력, 최근순 페이지 */
    generations: z.array(AdminInquiryGenerationSchema),
    pagination: z.object({
        page: z.number().int(),
        pageSize: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
// ── 회원 상세 종합 기록 (FIKET-452) ──
// GET /api/admin/users/:userId/ai-photos | /inquiries — users 메뉴 스코프(/api/admin/users prefix) 아래에 두어
// 다른 메뉴(ai-generations·ai-reference-quality·inquiries) 권한이 없는 MANAGER 도 회원 상세에서 볼 수 있게 한다.
/** 회원 상세 AI 사진 탭 — 문의 상세 AI 사진 Sheet 와 같은 응답 모양 */
export const AdminUserAiPhotosSchema = AdminInquiryAiPhotosSchema;
/** 회원이 남긴 고객 문의 목록(최근 활동순 페이지) */
export const AdminUserInquiriesSchema = z.object({
    userId: z.string(),
    items: z.array(AdminContactInquiryListItemSchema),
    pagination: z.object({
        page: z.number().int(),
        pageSize: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
//# sourceMappingURL=contact.js.map